import React, {useMemo, useState} from 'react'
import Text from '../../../../components/text/Text'
import {ILeaderDetail} from '../../../../apis/leader/getLeaderDetail'
import {useImage} from '../../../../utils/image'
import TableItem from '../../../../components/table/TableItem'
import TableHeader from '../../../../components/table/TableHeader'
import {useDisplayNumber} from '../../../../utils/number'
import {useTime} from '../../../../utils/time'
import {useColor, useTag} from '../../../../utils/etc'
import WizSelect from '../../../../components/select/WizSelect'
import ContainerLayout from '../../../../components/layout/ContainerLayout'
import useNavigation from '../../../../hooks/useNavigation'
import WizImage from '../../../../components/image/WizImage'
import {useString} from '../../../../utils/string'
import ic_arrow_down from '../../../../assets/images/ic_arrow_down.png'
import ic_arrow_up from '../../../../assets/images/ic_arrow_up.png'

interface IProps {
    className?: string
    leaderDetail?: ILeaderDetail
}

// admin/copy/leader/:id 의 copiers[]만 사용한다. 이 응답은 bot_score만 채워 보내므로
// 이동에 쓰는 bot id는 행의 id를 그대로 쓴다(bot.id는 항상 비어 있다).
// 카피어별 정산 정보(paid_leader / expected_pay / next_payment)는 admin API에 없다.
const LeaderCopierContainer: React.FC<IProps> = ({className, leaderDetail}) => {
    const {renderThumbnail} = useImage()
    const {floorBalance, renderPercent} = useDisplayNumber()
    const {localTimeString} = useTime()
    const {getNumberColor} = useColor()
    const {renderStateColor} = useTag()
    const {shortCutString} = useString()
    const {goCopierDetail} = useNavigation()

    const [copierSortState, setCopierSortState] = useState('running')

    const copierList = useMemo(() => {
        const list = [...(leaderDetail?.copiers ?? [])].sort(
            (a, b) => (b?.bot_score?.score ?? 0) - (a?.bot_score?.score ?? 0),
        )
        if (copierSortState === 'running') {
            return list.filter(copier => copier?.status === 'running')
        }
        return list
    }, [leaderDetail, copierSortState])

    return (
        <ContainerLayout className={className} title={'Copier List'}>
            <div>
                <WizSelect
                    optionKey="copierStatus"
                    value={copierSortState}
                    onChange={item => setCopierSortState(item as string)}
                    className={'md:mb-[20px]'}
                />
                <TableHeader className={'grid-cols-[auto_150px_150px] gap-x-[15px] mt-[10px] md:hidden'}>
                    <Text>Copier</Text>
                    <Text className={'text-center'}>Profit</Text>
                    <Text className={'text-right'}>Balance</Text>
                </TableHeader>
                {copierList?.map(copier => (
                    <TableItem
                        key={copier?.id}
                        onClick={() => goCopierDetail(copier?.id)}
                        className={
                            'grid-cols-[auto_150px_150px] gap-x-[15px] md:grid-cols-2 md:gap-x-0 md:pt-[20px] md:pb-[40px]'
                        }>
                        <div className={'md:text-center md:col-span-2'}>
                            <div className={'flex items-center gap-x-[10px] md:justify-center'}>
                                <WizImage
                                    src={renderThumbnail(copier?.user_thumbnail)}
                                    className={'w-[40px] h-[40px] rounded-full'}
                                />
                                <div>
                                    <Text className={'text-ti2'}>{shortCutString(copier?.user_name, 5)}</Text>
                                    <Text className={'text-body3 text-font03'}>{copier?.name}</Text>
                                </div>
                                <div>
                                    <Text className={`${renderStateColor(copier?.status === 'running')}`}>
                                        {copier?.status}
                                    </Text>
                                </div>
                            </div>

                            <div className={'mt-[5px] md:mt-[10px]'}>
                                <Text className={'text-body3 text-font03'}>
                                    Start At : {copier?.start_at ? localTimeString(copier?.start_at) : '-'}
                                </Text>
                                {copier?.pause_at && (
                                    <Text className={'text-body3 text-font_red'}>
                                        Paused At : {new Date(copier?.pause_at).toLocaleString()}
                                    </Text>
                                )}
                            </div>
                        </div>
                        <div className={'text-center md:my-[10px] md:space-y-[5px] md:border-r md:border-line03'}>
                            <Text className={'hidden md:block text-ti3 text-font03'}>Profit</Text>
                            <div>
                                <div className={'flex items-center gap-x-[3px] justify-center'}>
                                    <Text className={`text-ti2 ${getNumberColor(copier?.bot_score?.earning)}`}>
                                        {floorBalance(copier?.bot_score?.earning ?? 0)}
                                    </Text>
                                    <WizImage
                                        src={(copier?.bot_score?.earning ?? 0) >= 0 ? ic_arrow_up : ic_arrow_down}
                                        className={'w-[16px]'}
                                    />
                                </div>
                                <Text className={`text-body3 ${getNumberColor(copier?.bot_score?.score)}`}>
                                    ({renderPercent(copier?.bot_score?.score ?? 0)})
                                </Text>
                            </div>
                        </div>
                        <div className={'text-right md:my-[10px] md:text-center md:space-y-[5px]'}>
                            <Text className={'hidden md:block text-ti3 text-font03'}>Balance</Text>
                            <div>
                                <Text className={'text-ti2'}>{floorBalance(copier?.current_balance ?? 0)}</Text>
                            </div>
                        </div>
                    </TableItem>
                ))}

                {!copierList?.length && (
                    <TableItem className={'text-center'}>
                        <div>
                            <Text>No Items</Text>
                        </div>
                    </TableItem>
                )}
            </div>
        </ContainerLayout>
    )
}

export default LeaderCopierContainer
